import { useSnackbarStore } from './snackbar.store';
import type { Snackbar, UndoAction } from './types/snackbar.type';

export class SnackbarService {
  private store = useSnackbarStore();

  get snackbar(): Snackbar {
    return this.store.snackbar;
  }

  get undoAction(): UndoAction | undefined {
    return this.store.snackbar.undoAction;
  }

  set showUndo(show: boolean) {
    this.store.snackbar.showUndo = show;
  }

  public static showSnackbar(snackbar: Snackbar): void {
    const store = useSnackbarStore();

    // While a connection-issue toast is on screen, drop unrelated incoming snackbars.
    // Rationale: the user already knows the network is down — additional "Failed to
    // <do thing>" messages on top are noise, since none of those operations would
    // succeed anyway. The gate ends naturally when the connection toast dismisses
    // (auto-timeout or user click), so it has no false-positive tail past the toast's
    // visible lifetime.
    const incomingIsConnectionIssue = snackbar.kind === 'connection-issue';
    const connectionIssueOnScreen =
      store.snackbar.show === true && store.snackbar.kind === 'connection-issue';
    if (!incomingIsConnectionIssue && connectionIssueOnScreen) {
      return;
    }

    let color = '';
    let textCss = ' text-sm font-bold ';
    let prependIconCss = snackbar.prependIconCss;
    let appendIconCss = snackbar.appendIconCss;

    switch (snackbar.variant) {
      case 'caution-alt':
        color = 'caution-50';
        textCss = 'text-sm text-caution-800';
        prependIconCss = 'text-caution-800';
        appendIconCss = 'text-caution-800';
        snackbar.prependIcon = 'caution';
        break;
      case 'caution':
        color = 'shade-900';
        textCss += 'text-tint-0';
        snackbar.prependIcon = 'caution';
        break;
      case 'critical':
        color = 'shade-900';
        textCss += 'text-tint-0';
        snackbar.prependIcon = 'critical';
        break;
      case 'critical-alt':
        color = 'critical-50';
        textCss = 'text-sm text-critical-600';
        prependIconCss = 'text-critical-600';
        appendIconCss = 'text-critical-600';
        snackbar.prependIcon = 'critical-alt';
        snackbar.appendIcon = 'close';
        snackbar.horizontalPosition = 'left';
        break;
      case 'inform':
        color = 'inform-500';
        break;
      case 'primary':
        color = 'primary-500';
        break;
      case 'success':
        color = 'shade-900';
        textCss += 'text-tint-0';
        snackbar.prependIcon = 'success';
        prependIconCss = 'text-success-500';
        appendIconCss = 'text-success-500';
        break;
      default:
        color = 'shade-900';
        break;
    }

    store.snackbar = {
      ...snackbar,
      color,
      textCss,
      prependIconCss,
      appendIconCss,
      show: true,
    };
  }

  public static critical(text: string): void {
    SnackbarService.showSnackbar({
      variant: 'critical',
      text,
    });
  }
  public static criticalAlt(text: string): void {
    SnackbarService.showSnackbar({
      variant: 'critical-alt',
      text,
    });
  }

  public static success(text: string): void {
    SnackbarService.showSnackbar({
      variant: 'success',
      text,
    });
  }

  public static caution(text: string): void {
    SnackbarService.showSnackbar({
      variant: 'caution',
      text,
    });
  }
  public static cautionAlt(text: string): void {
    SnackbarService.showSnackbar({
      variant: 'caution-alt',
      text,
      appendIcon: 'close',
      horizontalPosition: 'left',
    });
  }

  /**
   * System-level toast for transient browser-fetch failures (`TypeError: Failed
   * to fetch`, etc.). Tag-based identity (`kind: 'connection-issue'`) lets
   * `showSnackbar` gate follow-up snackbars while this is on screen — see the
   * comment in that method for rationale.
   */
  public static connectionIssue(): void {
    SnackbarService.showSnackbar({
      kind: 'connection-issue',
      variant: 'caution-alt',
      text: 'Connection issue. Check your network and try again.',
      appendIcon: 'close',
      horizontalPosition: 'left',
    });
  }

  public hideSnackbar(): void {
    this.store.snackbar = {
      show: false,
      showUndo: false,
      undoAction: undefined,
    };
  }
}
